import { IS_PRODUCTION } from 'constants/isDevelopment'
import TagManager from 'react-gtm-module'
import { Pathology, PatologiaCadastroGtm } from 'types'
import { splitFullName } from 'utils/splitFullName'

class GoogleTagManagerTracker {
  private determineSpecialty(pathology?: string | null) {
    if (!pathology) return 'urologia'

    const urologyPathologies = [
      'erectile_dysfunction',
      'penis_enlargement',
      'premature_ejaculation',
      'prep_treatment',
      'prep',
      'prep_private',
      'hpb',
      'cystitis',
      'libido',
      'master-program',
      'control-program'
    ]
    const dermatologyPathologies = ['acne', 'hair_loss', 'dermatitis']

    if (urologyPathologies.includes(pathology)) {
      return 'urologia'
    }
    if (dermatologyPathologies.includes(pathology)) {
      return 'dermatologia'
    }
    return pathology === 'psychology' ? 'psicologia' : pathology
  }

  private unifyPrepTypes(pathology?: string | null) {
    if (pathology === 'prep_private') return 'prep'
    return pathology
  }

  public trackFinishedSurvey() {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'finalizou_questionário'
      }
    })
  }

  public trackFinishedSurveyMedleve() {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'pre_lead'
      }
    })
  }

  public trackValidatedRegistrationForm(
    completeName?: string,
    email?: string,
    phoneNumber?: string,
    patologia?: Pathology | PatologiaCadastroGtm
  ) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      nome: completeName,
      first_name: firstName,
      last_name: lastName,
      email,
      telefone: phoneNumber?.replace(/\D/g, ''),
      patologia
    }
    TagManager.dataLayer({
      dataLayer: {
        event: 'cadastro',
        data
      }
    })
  }

  public trackConsultationType(completeName?: string, phoneNumber?: string, consultationType?: string) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      nome: completeName,
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      telefone: phoneNumber?.replace(/\D/g, ''),
      tipo_de_consulta: consultationType
    }
    TagManager.dataLayer({
      dataLayer: {
        event: 'tipo_de_consulta',
        data
      }
    })
  }

  public trackSelectedDoctor(completeName?: string, phoneNumber?: string, email?: string) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      nome: completeName,
      telefone: phoneNumber?.replace(/\D/g, ''),
      email: email
    }
    TagManager.dataLayer({
      dataLayer: {
        event: 'agendamento_profissional',
        data
      }
    })
  }

  public trackReachedPaymentForm(
    firstName?: string,
    lastName?: string,
    phoneNumber?: string,
    email?: string,
    paymentType?: string
  ) {
    if (!IS_PRODUCTION) return

    const data = {
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      nome: `${firstName} ${lastName}`,
      telefone: phoneNumber?.replace(/\D/g, ''),
      email: email,
      payment_type: paymentType
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'add_payment_info',
        data
      }
    })
  }

  public trackPaymentSuccessful(
    completeName?: string,
    email?: string,
    phoneNumber?: string,
    consultationPrice?: string | number,
    consultationType?: string | null,
    pathology?: string | null,
    paymentType?: string | null,
    transactionId?: string | null,
    userId?: string | null
  ) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      nome: completeName?.toLowerCase(),
      email: email,
      telefone: phoneNumber?.replace(/\D/g, ''),
      value: String(consultationPrice),
      tipo_de_consulta: consultationType,
      patologia: this.unifyPrepTypes(pathology),
      payment_type: paymentType === 'pix' ? 'pix' : 'credit_card',
      especialidade: this.determineSpecialty(pathology),
      transaction_id: transactionId,
      user_id: userId
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'purchase_paid',
        data
      }
    })
  }

  public trackPostPurchaseQuestionnaireAnswered(
    completeName?: string,
    email?: string,
    phoneNumber?: string,
    medium?: string,
    spe?: string | null,
    patientZipCode?: string
  ) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      nome: completeName,
      email: email,
      telefone: phoneNumber?.replace(/\D/g, ''),
      cep: patientZipCode,
      especialidade: this.determineSpecialty(spe),
      tipo_de_consulta: medium,
      patologia: this.unifyPrepTypes(spe)
    }
    TagManager.dataLayer({
      dataLayer: {
        event: 'cadastro_after_purchase',
        data
      }
    })
  }

  public trackPatientSubmittedFormMessage(
    completeName?: string,
    email?: string,
    phoneNumber?: string,
    medium?: string,
    spe?: string | null,
    patientZipCode?: string
  ) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      nome: completeName,
      email: email,
      telefone: phoneNumber?.replace(/\D/g, ''),
      cep: patientZipCode,
      especialidade: this.determineSpecialty(spe),
      tipo_de_consulta: medium,
      patologia: this.unifyPrepTypes(spe)
    }
    TagManager.dataLayer({
      dataLayer: {
        event: 'finalizou_cadastro_mensagem',
        data
      }
    })
  }

  public trackSignUpForFreeEpisode(email?: string) {
    if (!IS_PRODUCTION || !email) return

    TagManager.dataLayer({
      dataLayer: {
        event: 'cadastro_episodio',
        email: email
      }
    })
  }

  public trackPageLoad(slug?: string) {
    if (!IS_PRODUCTION || !slug) return

    const eventName = slug.split('/').pop()
    TagManager.dataLayer({
      dataLayer: {
        event: eventName
      }
    })
  }

  // Course landings (/cursos/<slug>). `pagina_versao` stays in the GTM data model for the rest of the visit
  // (the checkout is a client-side navigation), so the funnel events can be split by landing version.
  public trackProgramLanding(event: string, data: Record<string, string | number>) {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event,
        ...data
      }
    })
  }

  // ECS-617 — Lilly manufacturer-program funnel for Mounjaro purchases: the
  // patient must self-register at Lilly before checking out, and we want to
  // measure how (and whether) that detour happens.
  public trackLillyLinkOpened() {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'lilly_link_opened'
      }
    })
  }

  public trackLillyWindowClosed(secondsOpen: number) {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'lilly_window_closed',
        data: { seconds_open: secondsOpen }
      }
    })
  }

  public trackLillyRegistrationSelfReported() {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'lilly_registration_selfreported'
      }
    })
  }

  // Medleve weight-loss reconsulta picker (me/new-consultation choose-medium):
  // measures which option returning patients pick, split by whether the
  // questionnaire renewal was offered to them.
  public trackNutritionOffer(action: 'viewed' | 'accepted' | 'dismissed', consultationId: number) {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: `nutrition_offer_${action}`,
        data: { consultation_id: consultationId }
      }
    })
  }

  public trackReconsultaOptionsViewed(renewalOffered: boolean) {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'reconsulta_options_viewed',
        data: { renewal_offered: renewalOffered }
      }
    })
  }

  public trackReconsultaOptionSelected(
    option: 'renewal' | 'chat' | 'video' | 'nutrition' | 'personal_training',
    renewalOffered: boolean
  ) {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'reconsulta_option_selected',
        data: { option, renewal_offered: renewalOffered }
      }
    })
  }

  public trackMenPowerCtaClick() {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'click_banner_men_power'
      }
    })
  }

  public trackWelcomePageButton() {
    if (!IS_PRODUCTION) return
    TagManager.dataLayer({
      dataLayer: {
        event: 'agendar_consulta'
      }
    })
  }

  public trackRegisterIsha(completeName: string, email: string, phoneNumber: string, patologia: string) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      email,
      phone: phoneNumber.replace(/\D/g, ''),
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      patology: patologia
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'cadastro',
        data
      }
    })
  }

  public trackPurchaseIsha(
    completeName: string,
    email: string,
    phoneNumber: string,
    value: string | number,
    transactionId: string | number,
    paymentType: string,
    patologia: string
  ) {
    if (!IS_PRODUCTION) return

    const { firstName, lastName } = splitFullName(completeName)

    const data = {
      email,
      phone: phoneNumber.replace(/\D/g, ''),
      first_name: firstName?.toLowerCase(),
      last_name: lastName?.toLowerCase(),
      patology: this.unifyPrepTypes(patologia),
      value: String(value),
      transaction_id: transactionId,
      payment_type: paymentType === 'pix' ? 'pix' : 'credit_card'
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'purchase',
        data
      }
    })
  }
}

export const gTagManagerTracker = new GoogleTagManagerTracker()
